/** Custom record forms where the generated one isn't good enough: EPD entry, breed makeup and pedigree corrections. */
import { useEffect, useMemo, useState } from "react";
import { localDate, type AnimalDetail, type ObservedTime, type SubjectRef } from "../../../contract";
import { SubjectsInput, WhenInput } from "../../../web/forms/EventForm";
import { RefPicker } from "../../../web/forms/pickers";
import { SchemaForm, issuesToErrors, type Errors, type Values } from "../../../web/forms/SchemaForm";
import { ApiError, api } from "../../../web/lib/api";
import { t, useLanguage } from "../../../web/lib/i18n";
import { useMe } from "../../../web/lib/session";
import type { EventFormProps } from "../../../web/module";
import { registry } from "../../../web/registry";
import { Button, Checkbox, Chip, Field, Icon, IconButton, Segmented, TextArea, TextInput, toast } from "../../../web/ui/kit";
import { CompositionBar, traitName, useTraits } from "./ui";

function useBaseState(props: EventFormProps) {
  const { data: me } = useMe();
  const today = me?.ranch ? localDate(new Date(), me.ranch.timezone) : new Date().toISOString().slice(0, 10);
  const [subjects, setSubjects] = useState<SubjectRef[]>(props.initial.subjects ?? []);
  const [occurred, setOccurred] = useState<ObservedTime>(props.initial.occurred ?? { value: today, precision: "date" });
  const [data, setData] = useState<Values>(props.initial.data ?? {});
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const submit = async (payload: Values) => {
    setBusy(true);
    setErrors({});
    try {
      await props.onSubmit({ type: props.initial.type, subjects, occurred, data: payload, place_id: props.initial.place_id ?? null, job_id: props.initial.job_id ?? null, batch_id: props.initial.batch_id ?? null }, reason || undefined);
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) setErrors(issuesToErrors(err.issues));
      else toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  return { subjects, setSubjects, occurred, setOccurred, data, setData, reason, setReason, errors, busy, submit };
}

function Actions({ existing, busy, onCancel, reason, setReason, disabled }: { existing?: unknown; busy: boolean; onCancel: () => void; reason: string; setReason: (v: string) => void; disabled?: boolean }) {
  return (
    <>
      {!!existing && <Field label={t("Why the change?")} optional><TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Wrong date, wrong animal…")} /></Field>}
      <div className="form-actions">
        <Button variant="primary" size="lg" type="submit" busy={busy} icon="check" disabled={disabled}>{existing ? t("Save correction") : t("Save")}</Button>
        <Button variant="ghost" onClick={onCancel}>{t("Cancel")}</Button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- EPDs

interface TraitRow { trait: string; value?: number; acc?: number; pct?: number }
const QUICK = ["CED", "BW", "WW", "YW", "MILK", "CEM", "STAY", "HPG", "MW", "MARB", "RE"];
const QUICK_INDEX = ["HB", "GM", "$M", "$W", "$B", "$C"];
const num = (v: string) => (v.trim() === "" || Number.isNaN(Number(v)) ? undefined : Number(v));

function TraitRows({ rows, onChange, kind, errors, prefix }: { rows: TraitRow[]; onChange: (r: TraitRow[]) => void; kind: "epd" | "index"; errors: Errors; prefix: string }) {
  const traits = useTraits();
  const options = (traits.data?.traits ?? []).filter((x) => x.kind === kind);
  const quick = (kind === "epd" ? QUICK : QUICK_INDEX).filter((k) => !rows.some((r) => r.trait === k));
  const set = (i: number, patch: Partial<TraitRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="stack" style={{ gap: 8 }}>
      {rows.map((r, i) => (
        <div key={i} className="genetics-epd-entry">
          <div className="genetics-row-head">
            <TextInput aria-label={t("Trait")} list={`genetics-${kind}-traits`} value={r.trait} onChange={(e) => set(i, { trait: e.target.value.trim() })} className="mono" style={{ width: 110 }} />
            <span className="field-hint" style={{ flex: 1 }}>{r.trait ? traitName(r.trait, traits.data?.traits) : ""}</span>
            <IconButton icon="delete" label={t("Remove")} onClick={() => onChange(rows.filter((_, j) => j !== i))} />
          </div>
          <div className="genetics-epd-values" style={{ gridTemplateColumns: kind === "epd" ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))" }}>
            <TextInput aria-label={t("Value")} inputMode="decimal" value={r.value ?? ""} onChange={(e) => set(i, { value: num(e.target.value) })} placeholder={t("Value")} />
            {kind === "epd" && <TextInput aria-label={t("Accuracy")} inputMode="decimal" value={r.acc ?? ""} onChange={(e) => set(i, { acc: num(e.target.value) })} placeholder={t("Acc")} />}
            <TextInput aria-label={t("Top %")} inputMode="decimal" value={r.pct ?? ""} onChange={(e) => set(i, { pct: num(e.target.value) })} placeholder={t("Top %")} />
          </div>
          {errors[`${prefix}.${i}.value`] && <p className="field-err">{errors[`${prefix}.${i}.value`]}</p>}
          {errors[`${prefix}.${i}.acc`] && <p className="field-err">{errors[`${prefix}.${i}.acc`]}</p>}
        </div>
      ))}
      <datalist id={`genetics-${kind}-traits`}>{options.map((o) => <option key={o.key} value={o.key}>{traitName(o.key, traits.data?.traits)}</option>)}</datalist>
      <div className="choice-chips">
        {quick.map((k) => <button key={k} type="button" className="choice" onClick={() => onChange([...rows, { trait: k }])}><Icon name="plus" size={14} /> {k}</button>)}
        <button type="button" className="choice" onClick={() => onChange([...rows, { trait: "" }])}>{t("Other")}</button>
      </div>
    </div>
  );
}

export function EvaluationForm(props: EventFormProps) {
  useLanguage();
  const { data: me } = useMe();
  const s = useBaseState(props);
  const def = registry.event("genetics.evaluation")!;
  const initial = props.initial.data as { traits?: TraitRow[]; indexes?: Array<{ index: string; value: number; pct?: number }>; association?: string } | undefined;
  const [traits, setTraits] = useState<TraitRow[]>(initial?.traits ?? []);
  const [indexes, setIndexes] = useState<TraitRow[]>((initial?.indexes ?? []).map((x) => ({ trait: x.index, value: x.value, pct: x.pct })));
  const fallback = (me?.ranch?.settings.genetics as { association?: string } | undefined)?.association;
  useEffect(() => { if (!s.data.association && fallback) s.setData({ ...s.data, association: fallback }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fallback]);
  const payload = () => ({
    ...s.data,
    traits: traits.filter((r) => r.trait && r.value !== undefined).map((r) => ({ trait: r.trait, value: r.value!, ...(r.acc !== undefined ? { acc: r.acc } : {}), ...(r.pct !== undefined ? { pct: r.pct } : {}) })),
    indexes: indexes.filter((r) => r.trait && r.value !== undefined).map((r) => ({ index: r.trait, value: r.value!, ...(r.pct !== undefined ? { pct: r.pct } : {}) })),
  });
  return (
    <form className="event-form" onSubmit={(e) => { e.preventDefault(); void s.submit(payload()); }}>
      <SubjectsInput type={def.type} value={s.subjects} onChange={s.setSubjects} errors={s.errors} labels={props.existing?.labels} />
      <WhenInput value={s.occurred} onChange={s.setOccurred} error={s.errors.occurred} />
      <SchemaForm fields={def.fields} value={s.data} onChange={s.setData} errors={s.errors} only={["association"]} />
      <Field label={t("EPDs")} hint={t("Type them as printed. Leave out what isn't listed; a blank is not zero.")} error={s.errors.traits}>
        <TraitRows rows={traits} onChange={setTraits} kind="epd" errors={s.errors} prefix="traits" />
      </Field>
      <Field label={t("Indexes")} optional>
        <TraitRows rows={indexes} onChange={setIndexes} kind="index" errors={s.errors} prefix="indexes" />
      </Field>
      <SchemaForm fields={def.fields} value={s.data} onChange={s.setData} errors={s.errors} only={["release", "kind", "genomic", "source", "note"]} />
      <Actions existing={props.existing} busy={s.busy} onCancel={props.onCancel} reason={s.reason} setReason={s.setReason} />
    </form>
  );
}

// ---------------------------------------------------------------- breed makeup

const BREEDS = ["Angus", "Red Angus", "Hereford", "Simmental", "Charolais", "Brahman", "Brangus", "Red Brangus", "Beefmaster", "Limousin", "Gelbvieh", "Shorthorn", "Wagyu", "Santa Gertrudis", "Holstein", "Jersey"];

export function CompositionForm(props: EventFormProps) {
  useLanguage();
  const s = useBaseState(props);
  const def = registry.event("genetics.breed_composition")!;
  const [rows, setRows] = useState<Array<{ breed: string; percent?: number }>>((props.initial.data?.components as Array<{ breed: string; percent: number }> | undefined) ?? [{ breed: "" }]);
  const [deriving, setDeriving] = useState(false);
  const animals = s.subjects.filter((x) => x.role === "animal");
  const filled = rows.filter((r) => r.breed.trim() && r.percent);
  const total = filled.reduce((n, r) => n + (r.percent ?? 0), 0);
  const unknown = Math.max(0, Math.round((100 - total) * 100) / 100);
  const derive = async () => {
    if (animals.length !== 1) return;
    setDeriving(true);
    try {
      const r = await api.get<{ components: Array<{ breed: string; percent: number }>; unknown: number; from: Record<string, { label: string; known: boolean } | null> }>(`/m/genetics/composition/${animals[0].id}/derive`);
      if (!r.components.length) toast(t("Neither parent has a breed makeup on record."), { tone: "warn" });
      else { setRows(r.components); s.setData({ ...s.data, basis: "parents" }); }
    } finally { setDeriving(false); }
  };
  return (
    <form className="event-form" onSubmit={(e) => { e.preventDefault(); void s.submit({ ...s.data, components: filled.map((r) => ({ breed: r.breed.trim(), percent: r.percent! })) }); }}>
      <SubjectsInput type={def.type} value={s.subjects} onChange={s.setSubjects} errors={s.errors} labels={props.existing?.labels} />
      <WhenInput value={s.occurred} onChange={s.setOccurred} error={s.errors.occurred} />
      <Field label={t("Breeds")} hint={t("Only what you know. The rest shows as unknown.")} error={s.errors.components}>
        <div className="stack" style={{ gap: 8 }}>
          {rows.map((r, i) => (
            <div key={i} className="genetics-comp-row">
              <TextInput aria-label={t("Breed")} list="genetics-breeds" value={r.breed} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, breed: e.target.value } : x)))} placeholder={t("Breed")} />
              <div className="input-suffix"><TextInput aria-label={t("Percent")} inputMode="decimal" value={r.percent ?? ""} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, percent: num(e.target.value) } : x)))} /><span className="suffix">%</span></div>
              <IconButton icon="delete" label={t("Remove")} onClick={() => setRows(rows.filter((_, j) => j !== i))} />
            </div>
          ))}
          <datalist id="genetics-breeds">{BREEDS.map((b) => <option key={b} value={b} />)}</datalist>
          <div className="row-actions">
            <Button size="sm" icon="plus" onClick={() => setRows([...rows, { breed: "" }])}>{t("Add a breed")}</Button>
            {[50, 25].map((p) => <button key={p} type="button" className="choice" onClick={() => setRows([...rows.filter((r) => r.breed || r.percent), { breed: "", percent: p }])}>{p}%</button>)}
            {animals.length === 1 && <Button size="sm" variant="ghost" icon="pedigree" busy={deriving} onClick={() => void derive()}>{t("Work out from parents")}</Button>}
          </div>
          {filled.length > 0 && (total > 100.01 ? <p className="field-err">{t("Breeds add up to more than 100%.")}</p> : <CompositionBar components={filled.map((r) => ({ breed: r.breed, percent: r.percent! }))} unknown={unknown} />)}
        </div>
      </Field>
      <SchemaForm fields={def.fields} value={s.data} onChange={s.setData} errors={s.errors} only={["basis", "note"]} />
      <Actions existing={props.existing} busy={s.busy} onCancel={props.onCancel} reason={s.reason} setReason={s.setReason} disabled={!filled.length || total > 100.01} />
    </form>
  );
}

// ---------------------------------------------------------------- pedigree correction

interface Check { cycle: boolean; sex_mismatch: boolean; born_after: boolean; current: string | null }

export function PedigreeCorrectionForm(props: EventFormProps) {
  useLanguage();
  const s = useBaseState(props);
  const def = registry.event("genetics.pedigree_correction")!;
  const role = (s.data.role as "sire" | "dam" | undefined) ?? "sire";
  const animalId = s.subjects.find((x) => x.role === "animal")?.id;
  const [unknown, setUnknown] = useState(false);
  const [animal, setAnimal] = useState<AnimalDetail | null>(null);
  const [check, setCheck] = useState<Check | null>(null);
  const parentId = s.data.parent_id as string | undefined;
  useEffect(() => {
    if (!animalId) { setAnimal(null); return; }
    let alive = true;
    api.get<AnimalDetail>(`/animals/${animalId}`).then((a) => { if (alive) setAnimal(a); }).catch(() => {});
    return () => { alive = false; };
  }, [animalId]);
  const current = role === "sire" ? animal?.sire : animal?.dam;
  useEffect(() => {
    if (!animalId || !parentId) { setCheck(null); return; }
    let alive = true;
    api.post<Check>("/m/genetics/pedigree/check", { animal_id: animalId, role, parent_id: parentId }).then((c) => { if (alive) setCheck(c); }).catch(() => {});
    return () => { alive = false; };
  }, [animalId, parentId, role]);
  const blocking = !!check?.cycle;
  const labels = useMemo(() => ({ ...(props.existing?.labels ?? {}), ...(current ? { [current.id]: current.label } : {}) }), [props.existing, current]);
  const payload = () => {
    const out: Values = { ...s.data, role };
    if (unknown) delete out.parent_id;
    if (current && !props.existing) out.previous_id = current.id;
    return out;
  };
  return (
    <form className="event-form" onSubmit={(e) => { e.preventDefault(); void s.submit(payload()); }}>
      <SubjectsInput type={def.type} value={s.subjects} onChange={s.setSubjects} errors={s.errors} labels={props.existing?.labels} />
      <Field label={t("Parent")}>
        <Segmented label={t("Parent")} value={role} onChange={(v) => s.setData({ ...s.data, role: v, parent_id: undefined })} options={[{ value: "sire", label: t("Sire") }, { value: "dam", label: t("Dam (genetic)") }]} />
      </Field>
      {animal && <p className="field-hint">{current ? t("Recorded now: {{parent}}", { parent: current.label }) : t("Nothing recorded now.")}</p>}
      <Field label={t("Correct parent")} error={s.errors.parent_id} hint={role === "dam" ? t("For an ET calf the genetic dam is the donor, not the recipient.") : undefined}>
        {!unknown && <RefPicker target="animal" value={parentId ?? null} onChange={(v) => s.setData({ ...s.data, parent_id: v ?? undefined })} filter={{ sex: role === "sire" ? "male" : "female", status: "all" }} labels={labels} />}
        <label className="genetics-pick"><Checkbox checked={unknown} onChange={(v) => { setUnknown(v); if (v) s.setData({ ...s.data, parent_id: undefined }); }} label={t("Unknown")} /> {t("Parent is unknown")}</label>
      </Field>
      {check?.cycle && <p className="field-err" role="alert">{t("That would make the animal its own ancestor.")}</p>}
      {check?.sex_mismatch && <p className="field-err" role="alert">{role === "sire" ? t("A sire has to be a male.") : t("A dam has to be a female.")}</p>}
      {check?.born_after && <p className="field-err" role="alert">{t("The parent was born after this animal.")}</p>}
      {parentId && current && parentId === current.id && <Chip tone="info" icon="info">{t("That's already the recorded parent.")}</Chip>}
      <SchemaForm fields={def.fields} value={s.data} onChange={s.setData} errors={s.errors} only={["basis"]} />
      <Field label={t("Why")} error={s.errors.reason}>
        <TextArea value={(s.data.reason as string) ?? ""} rows={3} onChange={(e) => s.setData({ ...s.data, reason: e.target.value })} placeholder={t("DNA report NG-55102 excluded B20")} />
      </Field>
      <Actions existing={props.existing} busy={s.busy} onCancel={props.onCancel} reason={s.reason} setReason={s.setReason} disabled={blocking || (!unknown && !parentId)} />
    </form>
  );
}
